import React from 'react';

interface ChartData {
  date: Date;
  clicked_links_total: number;
  opened_emails_total: number;
}

interface GroupedBarChartProps {
  data: ChartData[];
  loading?: boolean;
  timeScale: 'day' | 'week' | 'month' | 'year';
}

const formatValue = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export function GroupedBarChart({ data, loading, timeScale }: GroupedBarChartProps) {
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-gray-400">Carregando dados...</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-gray-400">Nenhum dado disponível para o período selecionado</span>
      </div>
    );
  }

  // Agrupa dados por dia para evitar duplicatas
  const groupedData = data.reduce((acc, curr) => {
    const dateKey = curr.date.toISOString().split('T')[0];
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: curr.date,
        clicked_links_total: curr.clicked_links_total,
        opened_emails_total: curr.opened_emails_total
      };
    } else {
      acc[dateKey].clicked_links_total = curr.clicked_links_total;
      acc[dateKey].opened_emails_total = curr.opened_emails_total;
    }
    return acc;
  }, {} as Record<string, ChartData>);

  // Pega apenas os últimos 7 registros
  const processedData = Object.values(groupedData)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(-7);

  // Como são percentuais, o valor máximo é 100
  const maxValue = 100;

  const yAxisSteps = 5;
  const yAxisValues = Array.from(
    { length: yAxisSteps },
    (_, i) => Math.round((maxValue / (yAxisSteps - 1)) * i)
  );

  const margin = { top: 20, right: 20, bottom: 40, left: 60 };
  const chartWidth = 800 - margin.left - margin.right;
  const chartHeight = 220 - margin.top - margin.bottom;

  // Calcula o tamanho ideal das barras
  const groupSpacing = chartWidth / (processedData.length * 1.2);
  const barWidth = groupSpacing * 0.35;
  const barGap = barWidth * 0.5;

  const formatDate = (date: Date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  // Calcula médias para linhas de referência
  const averageClicks = processedData.reduce((acc, item) => acc + item.clicked_links_total, 0) / processedData.length;
  const averageOpens = processedData.reduce((acc, item) => acc + item.opened_emails_total, 0) / processedData.length;

  // Calcula variações percentuais
  const getVariation = (current: number, average: number) => {
    if (average === 0) return 0;
    return ((current - average) / average) * 100;
  };

  return (
    <div className="h-full flex items-center justify-center px-2 text-gray-400">
      <div className="w-full h-full relative">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-[20px] bottom-[40px] flex flex-col justify-between text-xs text-gray-400">
          {yAxisValues.reverse().map((value, i) => (
            <div key={i} className="flex items-center h-6">
              <span className="pr-2 w-12 text-right">{formatValue(value)}</span>
            </div>
          ))}
        </div>

        {/* Chart area */}
        <div className="ml-[60px] h-full relative">
          <svg 
            className="w-full h-full text-gray-400" 
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            style={{
              marginTop: margin.top,
              marginBottom: margin.bottom,
              height: `calc(100% - ${margin.top + margin.bottom}px)`
            }}
          >
            {/* Grid lines */}
            {yAxisValues.map((_, i) => (
              <line
                key={i}
                x1="0"
                y1={i * (chartHeight / (yAxisValues.length - 1))}
                x2={chartWidth}
                y2={i * (chartHeight / (yAxisValues.length - 1))}
                stroke="#1F2937"
                strokeWidth="1"
              />
            ))}

            {/* Bars */}
            {processedData.map((item, i) => {
              const groupX = (i * chartWidth) / processedData.length;
              const centerX = groupX + (chartWidth / processedData.length) / 2;

              const clicksHeight = (item.clicked_links_total / maxValue) * chartHeight;
              const opensHeight = (item.opened_emails_total / maxValue) * chartHeight;

              return (
                <g key={i} className="group">
                  {/* Cliques */}
                  <rect
                    x={centerX - barWidth - barGap/2}
                    y={chartHeight - clicksHeight}
                    width={barWidth}
                    height={clicksHeight}
                    fill="#fb923c"
                    rx={4}
                    className="transition-all duration-300 opacity-80 hover:opacity-100"
                  />

                  {/* Aberturas */}
                  <rect
                    x={centerX + barGap/2}
                    y={chartHeight - opensHeight}
                    width={barWidth}
                    height={opensHeight}
                    fill="#4ADE80"
                    rx={4}
                    className="transition-all duration-300 opacity-80 hover:opacity-100"
                  />

                  {/* Tooltip */}
                  <g className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <rect
                      x={centerX - 50}
                      y={10}
                      width="100"
                      height="60"
                      rx="4"
                      fill="#1A1D23"
                      className="filter drop-shadow-lg"
                    />
                    <text
                      x={centerX}
                      y={30}
                      textAnchor="middle"
                      fill="#fff"
                      fontSize="12"
                    >
                      {formatDate(item.date)}
                    </text>
                    <text
                      x={centerX}
                      y={45}
                      textAnchor="middle"
                      fill="#fb923c"
                      fontSize="12"
                    >
                      {`Cliques: ${formatValue(item.clicked_links_total)}`}
                    </text>
                    <text
                      x={centerX}
                      y={60}
                      textAnchor="middle"
                      fill="#4ADE80"
                      fontSize="12"
                    >
                      {`Aberturas: ${formatValue(item.opened_emails_total)}`}
                    </text>
                  </g>
                </g>
              );
            })}
          </svg>

          {/* X-axis labels */}
          <div className="absolute bottom-0 left-0 right-0 h-8">
            <div className="relative w-full">
              {processedData.map((item, i) => {
                const x = (i * chartWidth) / processedData.length;
                const centerX = x + (chartWidth / processedData.length) / 2;

                return (
                  <div
                    key={i}
                    className="absolute text-xs text-gray-400 text-center transform -translate-x-1/2"
                    style={{
                      left: `${(centerX / chartWidth) * 100}%`,
                      width: '60px'
                    }}
                  >
                    {formatDate(item.date)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}